import React, { useEffect, useMemo, useState } from 'react';
import { Callout, H5, Intent } from '@blueprintjs/core';
import useUserContext from 'ecommerce/context/user';
import uuid from 'utils/uuid';

import useEditorContext from '../../../context/editorContext';
import useEditorItemContext from '../../../context/editorItemContext';
import eventsApi from '../../../api/events';
import NumberOfGenerationsSelect from '../../../components/ancestry/numberOfGenerationsSelect';
import EventListCard from '../../components/eventListCard';

import countries from './countries.json';
import EventFormControl from '../../components/eventFormControl';

const DEFAULT_VALUE = {
  COUNTRY: 'United States',
  TYPE: 'National',
};

const HolidaysPanel = () => {
  const { item } = useEditorItemContext();
  const editorContext = useEditorContext();
  const userContext = useUserContext();

  const [error, setError] = useState();

  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_VALUE.COUNTRY);
  const [selectedType, setSelectedType] = useState();

  const [types, setTypes] = useState([]);
  const [rawEvents, setEvents] = useState();

  const events = useMemo(() => {
    if (rawEvents) {
      if (selectedType) {
        return rawEvents.filter(event => event.type === selectedType);
      }
      return rawEvents;
    }

    return null;
  }, [rawEvents, selectedType]);

  useEffect(() => {
    (async () => {
      const { data, error: eventsError } = await eventsApi(
        userContext.accessToken,
      ).getHolidayEvents(countries[selectedCountry], item.additionalInfo.initialMonth);

      if (eventsError) {
        setError(eventsError);
        return;
      }

      setTypes(data.types);
      setSelectedType(data.types.find(t => t === DEFAULT_VALUE.TYPE) ?? data.types[0]);
      setEvents(data.events);
    })();
  }, [selectedCountry]);

  const handleOnAddEvents = selectedEvents => {
    editorContext.addEvents(
      item.id,
      selectedEvents.map(event => ({
        key: uuid(),
        date: event.date,
        description: event.description,
        type: 'holidays',
      })),
    );
  };

  if (error) {
    return (
      <>
        <Callout intent={Intent.DANGER}>
          Oh no! We’re sorry, something has gone wrong. We’re working to fix the issue. In the
          meantime, you could try refreshing your page.
        </Callout>
      </>
    );
  }

  return (
    <>
      <H5 className="mb-3">Select holidays to include</H5>

      <EventFormControl label="Country">
        <NumberOfGenerationsSelect
          fill
          values={Object.keys(countries)}
          selectedValue={selectedCountry}
          onChange={setSelectedCountry}
        />
      </EventFormControl>

      {types.length > 1 && (
        <EventFormControl label="Type">
          <NumberOfGenerationsSelect
            fill
            values={types}
            selectedValue={selectedType}
            onChange={setSelectedType}
          />
        </EventFormControl>
      )}

      <EventListCard events={events} dateMask="MMM D, YYYY" onAddEvents={handleOnAddEvents} />
    </>
  );
};

export default HolidaysPanel;
